import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { RouteEvent } from '@commons/models/domain/RouteEvent';
import { MapService } from '@commons/services/map/map/map.service';
import { ActivatedRoute } from '@angular/router';

import { Subscription, of, switchMap } from 'rxjs';
import { parseISO, format } from 'date-fns';

import { CountryCodeService } from '@commons/services/country.code/country-code.service';
import { FullAddressPipe } from '@commons/pipes/full-address.pipe';
import { VehicleService } from '@commons/services/vehicle/vehicle.service';
import { LanguageService } from '@shared/services/language.service';

@Component({
  selector: 'app-modal-with-park-information',
  templateUrl: './modal-with-park-information.component.html',
  styleUrls: ['./modal-with-park-information.component.scss'],
  providers: [FullAddressPipe],
})
export class ModalWithParkInformationComponent implements OnInit, OnDestroy {

  @ViewChild('container') public container: ElementRef<HTMLDivElement>;

  public isVisible: boolean;
  public data: RouteEvent;
  public formattedDate: any;
  public formattedStreet: string;
  public clickedVehicle: any;

  private onClickParkIconSubsc: Subscription;
  private onChangeClickedVehicleSubsc: Subscription;
  private onClickEventModalSubsc: Subscription;

  constructor(
    private mapService: MapService,
    private countryCodeService: CountryCodeService,
    private fullAddressPipe: FullAddressPipe,
    private vehicleService:  VehicleService,
    private activatedRoute: ActivatedRoute,
    private languageService: LanguageService,
  ) { }

  ngOnInit(): void {
    this.onClickParkIcon();
    this.onChangeClickedVehicle();
  }

  ngOnDestroy(): void {
    this.onClickParkIconSubsc?.unsubscribe();
    this.onChangeClickedVehicleSubsc?.unsubscribe();
    this.onClickEventModalSubsc?.unsubscribe();
  }

  public onClickHideModal(): void {
    this.isVisible = false;
    this.hideModal();
  }

  public copyCords(): void {
    const coords = `${this.data?.D.S.Lt},${this.data?.D.S.Ln}`;
    navigator.clipboard.writeText(coords);
  }

  private onChangeClickedVehicle(): void {
   this.onChangeClickedVehicleSubsc = this.activatedRoute.queryParams
   .pipe(
    switchMap((params) => {
      if (params['id']) {
        return this.vehicleService.getDetails$(Number(params['id']));
      }
      return of(null);
    })
   )
   .subscribe(
      (data) => {
        if (data) {
          this.onClickHideModal();
          this.clickedVehicle = { ...data }; 
          return;
        }
        this.onClickHideModal();
      }
    );
  }

  private onClickParkIcon(): void {
    this.onClickEventModalSubsc = this.mapService.onClickEventList$.subscribe((data) => {
      if (data) this.hideModal();
    });

    this.onClickParkIconSubsc = this.mapService.onClickParkStopIcon$.subscribe(
      (data) => {
        if (data) {
          this.data = {...data};
          this.formattedDate = this.formatEventDates(data);
          this.formattedStreet = this.onFormattedStreet(data);
  
          if (this.isVisible) {
            this.hideModal();
          }
          setTimeout(() => {
            this.showModal();
          }, this.isVisible ? 150 : 0);
          return;
        }
        this.hideModal();
      }
    );
  }
  
  private onFormattedStreet(data: RouteEvent): string {
    let dataStreet = { ...data.D.S };
    dataStreet.Cr = this.countryCodeService.getCode(dataStreet.Cr);
    const streetValue = this.fullAddressPipe.transform(dataStreet);
    return streetValue;
  }

  private hideModal(): void {
    this.container.nativeElement?.classList.add('hide');
    setTimeout(() => {
      this.container.nativeElement?.classList.remove('is-visible');
    }, 150);
  }

  private showModal(): void {
    this.isVisible = true;
    this.container.nativeElement?.classList.add('is-visible');
    this.container.nativeElement?.classList.remove('hide');
  }

  private formatEventDates(event: RouteEvent): { date: string, diff: any } {
    const { St, En } = event;
    const startDate = St && parseISO(St as any);
    const endDate = En && parseISO(En as any);
    const formatString = "d MMMM yyyy HH:mm";
    const startFormatted = St ? format(startDate, formatString, { locale: this.languageService.localeVariableDateFns }) : 'Invalid Date';
    const endFormatted = En ? format(endDate, formatString, { locale: this.languageService.localeVariableDateFns }) : 'Invalid date';

    return {
      date: `${startFormatted} - ${endFormatted}`,
      diff: this.calculateDetails(event),
    };
  }

  private calculateDetails(route: RouteEvent) {
    if (!route.St || !route.En) {
      return null;
    }
    else {
      const startTime = new Date(route.St).getTime();
      const endTime = new Date(route.En).getTime();
      const duration = Math.floor((endTime - startTime) / 1000);
      return duration;
    }
  }

}
